/**
 * <s-hscroll> — horizontal product slider.
 * Children: [data-hscroll-track] (scrollable list), [data-hscroll-prev], [data-hscroll-next].
 * Arrows scroll by one visible "page"; swipe/trackpad scroll natively. Arrows disable at each end.
 */
class SHscroll extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hscroll-track]');
    this.prev = this.querySelector('[data-hscroll-prev]');
    this.next = this.querySelector('[data-hscroll-next]');
    if (!this.track) return;
    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.onScroll = () => this.update();
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.enableMouseDrag();
    this.update();
  }
  /* Desktop: click and drag with a mouse to slide left/right (touch already swipes natively). */
  enableMouseDrag() {
    const track = this.track;
    let down = false, moved = false, startX = 0, startLeft = 0;
    track.style.cursor = 'grab';
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        track.style.cursor = 'grabbing';
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
      }
      if (moved) { e.preventDefault(); track.scrollLeft = startLeft - dx; }
    });
    const end = () => {
      if (!down) return;
      down = false;
      track.style.cursor = 'grab';
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointerleave', end);
    track.addEventListener('dragstart', (e) => { if (down) e.preventDefault(); });
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }
  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }
  go(direction) {
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * rtl * this.track.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
  }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    const pos = Math.abs(this.track.scrollLeft);
    if (this.prev) this.prev.disabled = pos <= 2;
    if (this.next) this.next.disabled = pos >= max;
  }
}
if (!customElements.get('s-hscroll')) customElements.define('s-hscroll', SHscroll);
